/**
 * The backup's take-over (D40: "the way out never depends on the broken server"). A bad state service on main does
 * not trip automatic failover — the productions' stream heartbeat is healthy — so the operator screen served by the
 * BACKUP offers a one-click take-over when main stops answering: its `/health` does not answer this page, the backup's
 * own peer link to it is down, or the last command did not get through. Underneath it is the emergency activation
 * (`forcePromoteProduction`, with its confirmation and reason), or — when main's state service still answers the pair
 * — a switchover asked of this node (`takeOverMutation`, `api/redundancy.ts`).
 *
 * Pure: the view feeds it the playout state's pair, the connectivity manager's endpoints and the last command.
 */
import type { PlayoutRedundancyWire } from '@acp/api-contract/playout.js';

export type TakeOverInput = {
  /** The pair as the answering node's process says (the playout state's `redundancy`); undefined outside a pair. */
  readonly redundancy: PlayoutRedundancyWire | undefined;
  /** The node this page talks to (the connectivity manager's primary), or null (the cloud). */
  readonly selfNodeId: string | null;
  /** Whether main's `/health` answers this page (its endpoint's probes), or null when the page does not probe it. */
  readonly mainAnswers: boolean | null;
  /** This node's own peer link to main, as its `/health` says, or null when unknown. */
  readonly peerLinkUp: boolean | null;
  /** The last command sent from this screen got no answer (`not-delivered`). */
  readonly lastCommandLost: boolean;
  readonly ended: boolean;
};

export type TakeOverOffer = { readonly offer: false } | { readonly offer: true; readonly mainNode: string; readonly why: string };

const NONE: TakeOverOffer = { offer: false };

export function takeOverOffer(input: TakeOverInput): TakeOverOffer {
  const pair = input.redundancy;
  if (input.ended || pair === undefined || input.selfNodeId === null) return NONE;
  // Only the backup's own screen: this page talks to the standby's node, and main (the other node) holds the claim.
  if (pair.role !== 'standby' || pair.node !== input.selfNodeId) return NONE;
  const main = pair.activeNode;
  if (main === null || main === input.selfNodeId) return NONE;
  if (input.mainAnswers === false) return { offer: true, mainNode: main, why: `main (${main}) does not answer this screen` };
  if (input.peerLinkUp === false) return { offer: true, mainNode: main, why: `this server has lost its link to main (${main})` };
  if (input.lastCommandLost) return { offer: true, mainNode: main, why: `the last command got no answer from main (${main})` };
  return NONE;
}

/** Whether a refusal of the emergency activation says main still answers the pair: then a switchover hands over instead. */
export function mainStillAnswers(message: string): boolean {
  return /the active node answers/.test(message);
}
